import React, { useRef, useState, useEffect } from "react";
import { Tooltip } from "@packages/ui/composites/Tooltip";
import type { InternalColumnDef } from "../schema/types";
import type { CellRenderProps, EditorRenderProps } from "../plugins/types";
import type { PluginRegistry } from "../plugins/PluginRegistry";
import { evaluateCondition } from "../../utils/evaluateCondition";
import { ActionIntentWrapper, ConditionalDisplayWrapper } from "./CellDisplayWrappers";

interface CellRendererProps {
  column: InternalColumnDef;
  row: Record<string, unknown>;
  rowIndex: number;
  isEditing: boolean;
  registry: PluginRegistry;
  injectedData?: Map<string, Array<{ label: string; value: string | number }>>;
  onDoubleClick?: () => void;
  editValue?: unknown;
  onEditChange?: (value: unknown) => void;
  onEditBlur?: () => void;
  editError?: string;
}

const TRUNCATE: React.CSSProperties = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const FallbackCell: React.FC<{ display: string }> = React.memo(({ display }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (el) setIsTruncated(el.scrollWidth > el.clientWidth);
  }, [display]);

  const inner = <div ref={ref} style={TRUNCATE}>{display}</div>;

  if (!isTruncated) return inner;

  return (
    <Tooltip.Root side="top" align="start" variant="dark" size="md">
      <Tooltip.Trigger asChild>{inner}</Tooltip.Trigger>
      <Tooltip.Content>{display}</Tooltip.Content>
    </Tooltip.Root>
  );
});

FallbackCell.displayName = 'FallbackCell';

const ALIGN_STYLES: Record<string, React.CSSProperties> = {
  center: { display: "flex", justifyContent: "center", alignItems: "center", width: "100%", minWidth: 0 },
  right: { display: "flex", justifyContent: "flex-end", alignItems: "center", width: "100%", minWidth: 0 },
};

export const CellRenderer: React.FC<CellRendererProps> = React.memo(({
  column,
  row,
  rowIndex,
  isEditing,
  registry,
  injectedData,
  onDoubleClick,
  editValue,
  onEditChange,
  onEditBlur,
  editError,
}) => {
  const groupedField = (row as Record<string, unknown>).__groupedField;
  if (groupedField != null && groupedField === column.field) {
    return <></>;
  }

  const whenPasses = column.editable?.when == null
    || evaluateCondition(column.editable.when, row);

  if (isEditing && column.editable && whenPasses) {
    const editor = registry.getEditor(column.editable.type);
    if (editor) {
      const editable: EditorRenderProps["column"]["editable"] = {
        type: column.editable.type,
        placeholder: column.editable.placeholder,
        options: [...column.editable.options],
        defaultValueField: column.editable.defaultValueField,
        validation: column.editable.validation,
        width: column.editable.width,
        multiple: column.editable.multiple,
      };
      return (
        <>
          {editor.render({
            value: editValue ?? row[column.field],
            onChange: onEditChange ?? (() => {}),
            onBlur: onEditBlur ?? (() => {}),
            column: {
              field: column.field,
              editable,
            },
            row: row as Record<string, unknown>,
            rowIndex,
            error: editError,
            injectedOptions: injectedData?.get(column.field),
          })}
        </>
      );
    }
  }

  const cellPlugin = (column.customRenderer && registry.getCellRenderer(column.customRenderer))
    || registry.getCellRenderer(column.type);
  let content: React.ReactNode;

  if (cellPlugin) {
    const props: CellRenderProps = {
      value: row[column.field],
      row: row as Record<string, unknown>,
      column: {
        field: column.field,
        header: column.header,
        type: column.type,
        align: column.align,
        format: column.format ? { ...column.format } : null,
        badge: column.badge ? { ...column.badge } : null,
        couple: column.couple ? { ...column.couple } : null,
        link: column.link ? { ...column.link } : null,
        mask: column.mask ? { ...column.mask } : null,
        sensitiveReveal: column.sensitiveReveal ? { ...column.sensitiveReveal } : null,
        modal: column.modal ? { ...(column.modal as Record<string, unknown>) } : null,
        dateRange: column.dateRange ? { ...column.dateRange } : null,
        conditionalValues: column.conditionalValues ? { ...column.conditionalValues } : null,
        fields: column.fields ? { ...column.fields } : null,
        prefix: column.prefix,
        uppercase: column.uppercase,
        emptyValue: column.emptyValue,
        width: { ...column.width },
        actionIntent: column.actionIntent ?? null,
        conditionalDisplay: column.conditionalDisplay ?? null,
      },
      rowIndex,
      isEditing: false,
      injectedData: injectedData?.get(column.field),
      onDoubleClick,
    };
    content = cellPlugin.render(props);
  } else {
    const val = row[column.field];
    const display = val != null ? String(val) : (column.emptyValue ?? "-");
    content = <FallbackCell display={display} />;
  }

  if (column.conditionalDisplay != null && column.conditionalDisplay.length > 0) {
    content = (
      <ConditionalDisplayWrapper
        rules={column.conditionalDisplay}
        row={row as Record<string, unknown>}
        baseContent={content}
        columnType={column.type}
      />
    );
  }

  if (column.actionIntent != null) {
    content = (
      <ActionIntentWrapper
        config={column.actionIntent}
        row={row as Record<string, unknown>}
        value={row[column.field]}
      >
        {content}
      </ActionIntentWrapper>
    );
  }

  const alignStyle = column.align ? ALIGN_STYLES[column.align] : undefined;
  if (alignStyle) {
    return <div style={alignStyle}>{content}</div>;
  }
  return <>{content}</>;
});

CellRenderer.displayName = 'CellRenderer';
